'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';

import { api } from '@/lib/axios';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  // --- Nuevas funciones de comprobación ---
  isAdmin: boolean;
  hasRole: (role: string | string[]) => boolean;
  canAccessRaceway: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  loading: true,
  login: async () => false,
  logout: async () => {},
  checkAuth: async () => {},
  isAdmin: false,
  hasRole: () => false,
  canAccessRaceway: false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // 🧠 Verificar autenticación con el backend
  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
      }
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.log('No authenticated session found');
      }
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (mounted) await checkAuth();
    };
    init();
    return () => { mounted = false; };
  }, []);

  // 🔑 Iniciar sesión
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await api.post('/auth/login', { username, password });
      if (response.data.success) {
        const { user: userData } = response.data;
        setUser(userData);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Login error:', error.response?.data || error.message);
      return false;
      throw error;
    }
  };

  // 🚪 Cerrar sesión
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // ⭐ Lógica de permisos centralizada
  const isAdmin = useMemo(() => user?.role === 'ADMIN', [user]);

  const hasRole = (role: string | string[]) => {
    if (!user) return false;
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    return user.role === role;
  };

  const canAccessRaceway = useMemo(() => {
    return !!user && (user.role === 'ADMIN' || user.role === 'RACEWAY_USER');
  }, [user]);

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        isAuthenticated, 
        loading, 
        login, 
        logout, 
        checkAuth,
        isAdmin,
        hasRole,
        canAccessRaceway 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);